import axios from "axios";

const isLocal =
  window.location.hostname === "localhost" ||
  window.location.hostname === "127.0.0.1";

const API_URL = isLocal ? "http://127.0.0.1:1601/api" : "/nahaam-api/api";

const api = axios.create({
  baseURL: API_URL,
  headers: {
    "Content-Type": "application/json",
  },
});

api.interceptors.request.use((config) => {
  const token = localStorage.getItem("token");
  if (token) {
    config.headers.Authorization = `Bearer ${token}`;
  }
  return config;
});

api.interceptors.response.use(
  (response) => response,
  (error) => {
    if (
      error.response &&
      error.response.status === 401 &&
      error.config &&
      !error.config.url.includes("/auth/")
    ) {
      localStorage.removeItem("token");
      localStorage.removeItem("user");
      window.location.href = "/login";
    }
    return Promise.reject(error);
  },
);

export const authService = {
  login: async (nationalCode: string, password: string) => {
    const response = await api.post("/auth/login", { nationalCode, password });
    if (response.data.token) {
      localStorage.setItem("token", response.data.token);
      localStorage.setItem("user", JSON.stringify(response.data.user));
    }
    return response.data;
  },
  sendBaleOtp: async (nationalCode: string) => {
    const response = await api.post("/auth/send-bale-otp", { nationalCode });
    return response.data;
  },
  loginBaleOtp: async (nationalCode: string, code: string) => {
    const response = await api.post("/auth/login-bale-otp", {
      nationalCode,
      code,
    });
    if (response.data.token) {
      localStorage.setItem("token", response.data.token);
      localStorage.setItem("user", JSON.stringify(response.data.user));
    }
    return response.data;
  },
  changePasswordFirstLogin: async (data: any) => {
    const response = await api.post("/auth/change-password-first-login", data);
    return response.data;
  },
  logout: () => {
    localStorage.removeItem("token");
    localStorage.removeItem("user");
  },
};

export const usersService = {
  getUsers: async () => {
    const response = await api.get("/users");
    return response.data;
  },
  createUser: async (userData: any) => {
    const response = await api.post("/users", userData);
    return response.data;
  },
  updateUser: async (id: number, data: any) => {
    const response = await api.put(`/users/${id}`, data);
    return response.data;
  },
  updateUserPermissions: async (id: number, permissions: string[]) => {
    const response = await api.put(`/users/${id}/permissions`, { permissions });
    return response.data;
  },
  deleteUser: async (id: number) => {
    const response = await api.delete(`/users/${id}`);
    return response.data;
  },
  toggleUserActivation: async (id: number, is_active: boolean) => {
    const response = await api.put(`/users/${id}/activation`, { is_active });
    return response.data;
  },
};

export const loanTypesService = {
  getLoanTypes: async () => {
    const response = await api.get("/loan-types");
    return response.data;
  },
  createLoanType: async (data: { name: string }) => {
    const response = await api.post("/loan-types", data);
    return response.data;
  },
  deleteLoanType: async (id: number) => {
    const response = await api.delete(`/loan-types/${id}`);
    return response.data;
  },
};

export const loansService = {
  getLoans: async (params?: {
    sort?: string;
    order?: "asc" | "desc";
    status?: string;
    my_loans?: boolean | string;
  }) => {
    const query = new URLSearchParams();
    if (params?.sort) query.append("sort", params.sort);
    if (params?.order) query.append("order", params.order);
    if (params?.status) query.append("status", params.status);
    if (params?.my_loans) query.append("my_loans", String(params.my_loans));
    const qs = query.toString();
    const response = await api.get(`/loans${qs ? `?${qs}` : ""}`);
    return response.data;
  },
  getMyLoans: async () => {
    const response = await api.get("/my-loans");
    return response.data;
  },
  cancelLoan: async (id: number) => {
    const response = await api.post(`/my-loans/${id}/cancel`);
    return response.data;
  },
  getAvailableDate: async (amount: number) => {
    const response = await api.get(`/loans/available-date?amount=${amount}`);
    return response.data;
  },
  createLoan: async (loanData: {
    amount: number;
    requested_date: string;
    description?: string;
    collateral?: string;
    percentage?: number;
    installments_count?: number;
  }) => {
    const response = await api.post("/loans", loanData);
    return response.data;
  },
  updateLoanStatus: async (
    id: number,
    status:
      | "pending"
      | "waiting_user_approval"
      | "user_approved"
      | "approved_registration"
      | "fee_paid"
      | "rejected"
      | "paid"
      | "cancelled",
    data?: any,
  ) => {
    const response = await api.put(`/loans/${id}/status`, { status, ...data });
    return response.data;
  },
  respondToLoan: async (
    id: number,
    data: {
      action: "accept" | "counter";
      percentage?: number;
      installments_count?: number;
      notes?: string;
    },
  ) => {
    const response = await api.post(`/loans/${id}/respond`, data);
    return response.data;
  },
  uploadReceipt: async (id: number, receipt_image: string) => {
    const response = await api.post(`/loans/${id}/receipt`, { receipt_image });
    return response.data;
  },
  payLoan: async (id: number) => {
    const response = await api.post(`/loans/${id}/pay`);
    return response.data;
  },
};

export const dashboardService = {
  getStats: async () => {
    const response = await api.get("/dashboard/stats");
    return response.data;
  },
};

export default api;

export const installmentsService = {
  getAdminInstallments: () => api.get("/installments/admin"),
  approveInstallment: (id: number, data: { penalty_amount: number }) =>
    api.post(`/installments/${id}/approve`, data),
  uploadInstallmentReceipt: (id: number, receipt_image: string) =>
    api.post(`/installments/${id}/receipt`, { receipt_image }),
};

export const expensesService = {
  getExpenseTypes: async () => {
    const response = await api.get("/expenses/types");
    return response.data;
  },
  getExpenses: async () => {
    const response = await api.get("/expenses");
    return response.data;
  },
  createExpense: async (data: {
    expense_type_id: number;
    amount: number;
    expense_date: string;
  }) => {
    const response = await api.post("/expenses", data);
    return response.data;
  },
};

export const investmentsService = {
  getInvestments: async () => {
    const response = await api.get("/investments");
    return response.data;
  },
  createInvestment: async (data: {
    week: number;
    amount: number;
    investment_date: string;
    user_ids?: number[];
  }) => {
    const response = await api.post("/investments", data);
    return response.data;
  },

  createIndividualInvestment: async (data: {
    user_id: number;
    intrinsic_amount: number;
    value_added_amount: number;
    investment_date: string;
  }) => {
    const response = await api.post("/investments/individual", data);
    return response.data;
  },
  getInvestmentSummary: async (user_id: number) => {
    const response = await api.get(`/investments/summary/${user_id}`);
    return response.data;
  },
};

export const balanceService = {
  getTransactions: async () => {
    const response = await api.get("/balance");
    return response.data;
  },
  getSummary: async () => {
    const response = await api.get("/balance/summary");
    return response.data;
  },
};

export const weeklyPenaltiesService = {
  getWeeklyPenalties: async () => {
    const response = await api.get("/weekly-penalties");
    return response.data;
  },
  createWeeklyPenalty: async (data: {
    user_id: number;
    amount: number;
    penalty_date: string;
    week?: number;
    description?: string;
  }) => {
    const response = await api.post("/weekly-penalties", data);
    return response.data;
  },
  deleteWeeklyPenalty: async (id: number) => {
    const response = await api.delete(`/weekly-penalties/${id}`);
    return response.data;
  },
};
